import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { api } from '../../api/client';
import MessageComposer from '../Chat/MessageComposer';
import { getAvatarColor } from '../../utils/avatar';
import {
    AlertCircle,
    AlertTriangle,
    Circle,
    Calendar,
    MessageSquare,
    FolderOpen,
    Check,
    Users,
    Eye,
    Search,
    Plus,
    Trash2,
    GripVertical,
    CalendarDays,
    History,
    ClipboardList,
    Edit2,
    Download,
    File as FileIcon,
    X,
    MoreVertical
} from 'lucide-react';
import { TicketStatus, Subtask } from '../../types';
import ConfirmModal from '../Modals/ConfirmModal';
import { Markdown } from '../../utils/markdown';

export default function TicketDetail({ ticket }: { ticket: any }) {
    const { updateTicket, deleteTicket, users, departments, settings, currentUser, isAdmin } = useApp();
    const [messages, setMessages] = useState<any[]>([]);
    const [draggedSubtask, setDraggedSubtask] = useState<string | null>(null);
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
    const [isEditingDesc, setIsEditingDesc] = useState(false);
    const [descDraft, setDescDraft] = useState('');
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [openCommentMenuId, setOpenCommentMenuId] = useState<string | null>(null);
    const commentMenuRef = useRef<HTMLDivElement>(null);

    const [assigneeSearchOpen, setAssigneeSearchOpen] = useState(false);
    const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
    const assigneeSearchRef = useRef<HTMLDivElement>(null);

    const [collabSearchOpen, setCollabSearchOpen] = useState(false);
    const [collabSearchTerm, setCollabSearchTerm] = useState('');
    const collabSearchRef = useRef<HTMLDivElement>(null);
    const routingDepartment = departments.find(department =>
        department.id === (ticket.assigned_to_department_id || ticket.department_id)
    );
    const currentUserDepartmentIds = new Set([
        ...(currentUser?.department_ids || []),
        ...(currentUser?.department_id ? [currentUser.department_id] : [])
    ]);
    const isRoutingDepartmentHead = Boolean(
        routingDepartment?.head_ids?.includes(currentUser?.id || '')
    );
    const canManageRouting = Boolean(
        isAdmin || currentUser?.id === ticket.created_by || isRoutingDepartmentHead
    );
    const canAssignIndividuals = Boolean(
        isAdmin ||
        isRoutingDepartmentHead ||
        !routingDepartment ||
        routingDepartment.allow_external_individual_assignment !== false ||
        currentUserDepartmentIds.has(routingDepartment.id)
    );

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (assigneeSearchRef.current && !assigneeSearchRef.current.contains(event.target as Node)) {
                setAssigneeSearchOpen(false);
            }
            if (collabSearchRef.current && !collabSearchRef.current.contains(event.target as Node)) {
                setCollabSearchOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close comment menu on outside click
    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            if (commentMenuRef.current && !commentMenuRef.current.contains(e.target as Node)) {
                setOpenCommentMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, []);

    useEffect(() => {
        if (ticket) {
            loadMessages();
        }
    }, [ticket?.id]);

    const loadMessages = async () => {
        if (!ticket) return;
        const msgs = await api.getTicketMessages(ticket.id);
        setMessages(msgs);
    };

    const handleSendMessage = async (content: string, _encryption?: boolean, attachments?: any[]) => {
        // Ticket messages are generally not encrypted in this context yet, but passing params for compatibility
        const newMessage = await api.sendMessage({
            content,
            ticket_id: ticket.id,
            attachments: attachments || []
        });
        setMessages(prev => [...prev, newMessage]);
    };

    const handleUpdateComment = async (commentId: string, content: string, _encryption?: boolean, attachments?: any[]) => {
        try {
            const updated = await api.editMessage(commentId, { content, attachments: attachments || [] });
            setMessages(prev => prev.map(m => m.id === commentId ? { ...m, ...updated } : m));
            setEditingCommentId(null);
        } catch (err) {
            console.error('Failed to update comment:', err);
        }
    };

    const handleDeleteComment = async (commentId: string) => {
        if (!window.confirm('Are you sure you want to delete this comment?')) return;
        try {
            await api.deleteMessage(commentId);
            setMessages(prev => prev.filter(m => m.id !== commentId));
        } catch (err) {
            console.error('Failed to delete comment:', err);
        }
    };

    const handleStatusChange = async (newStatus: string) => {
        await updateTicket(ticket.id, { status: newStatus as TicketStatus });
    };

    const handleAssigneeChange = async (userId: string) => {
        const currentAssigneeIds = ticket.assignees?.map((a: any) => a.id) || [];
        let newAssigneeIds;

        if (currentAssigneeIds.includes(userId)) {
            // Unassigning
            newAssigneeIds = currentAssigneeIds.filter((id: string) => id !== userId);
        } else {
            // Assigning
            if (settings?.allow_multiple_assignees) {
                newAssigneeIds = [...currentAssigneeIds, userId];
            } else {
                // If single assignee only, replace the current one
                newAssigneeIds = [userId];
            }
        }

        await updateTicket(ticket.id, { assignee_ids: newAssigneeIds });
    };

    const handleCollaboratorChange = async (userId: string) => {
        const currentCollaboratorIds = ticket.collaborators?.map((c: any) => c.id) || [];
        let newCollaboratorIds;

        if (currentCollaboratorIds.includes(userId)) {
            newCollaboratorIds = currentCollaboratorIds.filter((id: string) => id !== userId);
        } else {
            newCollaboratorIds = [...currentCollaboratorIds, userId];
        }

        await updateTicket(ticket.id, { collaborator_ids: newCollaboratorIds });
    };

    const handleAddSubtask = async () => {
        const newSubtask: Subtask = {
            id: Math.random().toString(36).substr(2, 9),
            title: '',
            completed: false
        };
        const updatedSubtasks = [...(ticket.subtasks || []), newSubtask];
        await updateTicket(ticket.id, { subtasks: updatedSubtasks });
    };

    const handleToggleSubtask = async (subtaskId: string) => {
        const updatedSubtasks = (ticket.subtasks || []).map((t: Subtask) =>
            t.id === subtaskId ? { ...t, completed: !t.completed } : t
        );
        await updateTicket(ticket.id, { subtasks: updatedSubtasks });
    };

    const handleDeleteSubtask = async (subtaskId: string) => {
        const updatedSubtasks = (ticket.subtasks || []).filter((t: Subtask) => t.id !== subtaskId);
        await updateTicket(ticket.id, { subtasks: updatedSubtasks });
    };

    const handleSubtaskChange = async (subtaskId: string, title: string) => {
        const updatedSubtasks = (ticket.subtasks || []).map((t: Subtask) =>
            t.id === subtaskId ? { ...t, title } : t
        );
        await updateTicket(ticket.id, { subtasks: updatedSubtasks });
    };

    const handleSubtaskReorder = async (draggedId: string, droppedOnId: string) => {
        const subtasks = [...(ticket.subtasks || [])];
        const draggedIndex = subtasks.findIndex(s => s.id === draggedId);
        const droppedIndex = subtasks.findIndex(s => s.id === droppedOnId);
        if (draggedIndex === -1 || droppedIndex === -1 || draggedIndex === droppedIndex) return;
        const [dragged] = subtasks.splice(draggedIndex, 1);
        subtasks.splice(droppedIndex, 0, dragged);
        await updateTicket(ticket.id, { subtasks });
    };

    const triggerCompletionAnimation = () => {
        // Flying icon
        const icons = ['🦄', '🚀', '✨', '🎉', '🌟', '🏆', '🏄'];
        const emoji = icons[Math.floor(Math.random() * icons.length)];
        const el = document.createElement('div');
        el.innerText = emoji;
        el.style.position = 'fixed';
        el.style.fontSize = '4rem';
        el.style.zIndex = '9999';
        el.style.pointerEvents = 'none';
        el.style.transition = 'all 1.5s cubic-bezier(0.25, 1, 0.5, 1)';

        // Start position (bottom left)
        el.style.left = '-100px';
        el.style.top = '100vh';
        el.style.transform = 'rotate(-45deg)';
        document.body.appendChild(el);

        // Force reflow
        void el.offsetWidth;

        // End position (top right arc)
        el.style.left = '100vw';
        el.style.top = '-100px';
        el.style.transform = 'rotate(45deg)';

        setTimeout(() => el.remove(), 1500);

        // Confetti burst
        for (let i = 0; i < 40; i++) {
            const confetti = document.createElement('div');
            const colors = ['#e01e5a', '#36c5f0', '#2eb67d', '#ecb22e', '#E63946', '#457B9D'];
            confetti.style.position = 'fixed';
            confetti.style.left = '50vw';
            confetti.style.top = '50vh';
            confetti.style.width = Math.random() * 10 + 5 + 'px';
            confetti.style.height = Math.random() * 10 + 5 + 'px';
            confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
            confetti.style.zIndex = '9998';
            confetti.style.pointerEvents = 'none';
            confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '4px';
            confetti.style.transition = 'all 1s cubic-bezier(0.25, 1, 0.5, 1)';

            const angle = Math.random() * Math.PI * 2;
            const velocity = 150 + Math.random() * 300;
            const tx = Math.cos(angle) * velocity;
            const ty = Math.sin(angle) * velocity + 100; // slight gravity pull

            document.body.appendChild(confetti);
            void confetti.offsetWidth;

            confetti.style.transform = `translate(${tx}px, ${ty}px) rotate(${Math.random() * 720}deg)`;
            confetti.style.opacity = '0';

            setTimeout(() => confetti.remove(), 1000);
        }
    };

    if (!ticket) {
        return (
            <div className="chat-area">
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <ClipboardList size={48} strokeWidth={1} opacity={0.3} />
                    </div>
                    <h3 className="empty-state-title">Ticket not found</h3>
                </div>
            </div>
        );
    }

    const getInitials = (name: string) => {
        return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
    };

    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getPriorityIcon = (priority: string) => {
        const props = { size: 14, strokeWidth: 2 };
        switch (priority) {
            case 'urgent': return <AlertTriangle {...props} color="var(--accent-danger)" />;
            case 'high': return <AlertCircle {...props} color="var(--color-warning)" />;
            case 'medium': return <Circle {...props} color="var(--accent-info)" />;
            case 'low': return <Circle {...props} color="var(--text-muted)" opacity={0.5} />;
            default: return null;
        }
    };

    const statuses = ['open', 'in_progress', 'resolved', 'closed'];

    return (
        <div className="chat-area">
            {/* Header */}
            <header className="chat-header ticket-detail-header">
                <div className="ticket-detail-title-row">
                    {isEditingTitle ? (
                        <div style={{ display: 'flex', gap: 8, flex: 1, alignItems: 'center' }}>
                            <input
                                className="input"
                                style={{ flex: 1, fontSize: 'var(--font-size-lg)', fontWeight: 600 }}
                                value={titleDraft}
                                onChange={(e) => setTitleDraft(e.target.value)}
                                autoFocus
                            />
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                    updateTicket(ticket.id, { title: titleDraft });
                                    setIsEditingTitle(false);
                                }}
                            >
                                <Check size={16} />
                            </button>
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setIsEditingTitle(false)}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    ) : (
                        <h1 className="chat-header-name" style={{ flex: 1, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <ClipboardList size={24} strokeWidth={1.5} color="var(--accent-primary)" />
                            {ticket.title}
                            {(isAdmin || currentUser?.id === ticket.created_by) && (
                                <button
                                    className="btn btn-ghost btn-sm btn-icon"
                                    onClick={() => {
                                        setTitleDraft(ticket.title || '');
                                        setIsEditingTitle(true);
                                    }}
                                    style={{ opacity: 0.8 }}
                                >
                                    <Edit2 size={14} />
                                </button>
                            )}
                        </h1>
                    )}
                    {(isAdmin || currentUser?.id === ticket.created_by) && (
                        <button
                            className="btn btn-ghost btn-sm btn-icon"
                            style={{ color: 'var(--accent-danger)' }}
                            onClick={() => setShowDeleteConfirm(true)}
                            title="Delete Ticket"
                        >
                            <Trash2 size={16} />
                        </button>
                    )}

                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span className="ticket-detail-id" style={{ whiteSpace: 'nowrap' }}>
                            #{ticket.ticket_number || ticket.id.slice(0, 4)}
                        </span>

                        {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                            <button
                                className="btn btn-primary btn-sm"
                                style={{
                                    background: 'linear-gradient(135deg, #2eb67d 0%, #36c5f0 100%)',
                                    border: 'none',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    boxShadow: '0 2px 8px rgba(46, 182, 125, 0.3)'
                                }}
                                onClick={() => {
                                    handleStatusChange('resolved');
                                    triggerCompletionAnimation();
                                }}
                                title="Mark Complete"
                            >
                                <Check size={16} strokeWidth={2.5} /> Complete
                            </button>
                        )}
                    </div>
                </div>

                {/* Ticket meta row */}
                <div className="ticket-meta-row">
                    {/* Status dropdown */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                        <span className="text-muted text-sm">Status:</span>
                        <select
                            className="input select"
                            value={ticket.status}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            style={{
                                padding: '4px 28px 4px 8px',
                                fontSize: 'var(--font-size-sm)',
                                minWidth: 120
                            }}
                        >
                            {statuses.map(s => (
                                <option key={s} value={s}>{s.replace('_', ' ')}</option>
                            ))}
                        </select>
                    </div>

                    {/* Priority badge */}
                    <span className={`priority-badge ${ticket.priority}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {getPriorityIcon(ticket.priority)}
                        {ticket.priority}
                    </span>

                    {/* Department */}
                    {ticket.department && (
                        <span style={{
                            fontSize: 'var(--font-size-sm)',
                            color: ticket.department.color,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                        }}>
                            <FolderOpen size={14} strokeWidth={1.5} />
                            {ticket.department.name}
                        </span>
                    )}

                    {/* Created date */}
                    <span className="text-muted text-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={14} strokeWidth={1.5} />
                        {formatDate(ticket.created_at)}
                    </span>
                </div>
            </header>

            {/* Content area split */}
            <div className="ticket-detail-layout">
                {/* Chat section */}
                <div className="ticket-detail-main">
                    {/* Messages list with internal scroll */}
                    <div className="message-list" style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                        minHeight: 0 // Crucial for flex nested scroll
                    }}>
                        {ticket.description && (
                            <div className="message" style={{
                                background: 'var(--bg-tertiary)',
                                padding: 'var(--spacing-md)',
                                borderRadius: 'var(--radius-lg)',
                                marginBottom: 'var(--spacing-md)',
                                flexShrink: 0
                            }}>
                                <div className="message-content">
                                    <div className="message-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span className="message-author">Description</span>
                                        {(isAdmin || currentUser?.id === ticket.created_by) && !isEditingDesc && (
                                            <button
                                                className="btn btn-ghost btn-icon btn-sm"
                                                onClick={() => {
                                                    setDescDraft(ticket.description || '');
                                                    setIsEditingDesc(true);
                                                }}
                                                title="Edit description"
                                                style={{ opacity: 0.8 }}
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                    {isEditingDesc ? (
                                        <div style={{ marginTop: 8 }}>
                                            <textarea
                                                className="input"
                                                style={{ width: '100%', minHeight: 100, marginBottom: 8 }}
                                                value={descDraft}
                                                onChange={(e) => setDescDraft(e.target.value)}
                                                onPaste={async (e) => {
                                                    if (!e.clipboardData) return;
                                                    const items = Array.from(e.clipboardData.items);
                                                    const imageItem = items.find(item => item.type.startsWith('image/'));
                                                    if (!imageItem) return;

                                                    e.preventDefault();
                                                    const blob = imageItem.getAsFile();
                                                    if (!blob) return;

                                                    const ext = imageItem.type.split('/')[1] || 'png';
                                                    const fileName = `paste-${Date.now()}.${ext}`;
                                                    const file = new File([blob], fileName, { type: imageItem.type });
                                                    const placeholder = `\n![Uploading ${fileName}...]()\n`;
                                                    const cursorPos = (e.target as HTMLTextAreaElement).selectionStart;
                                                    const textBefore = descDraft.substring(0, cursorPos);
                                                    const textAfter = descDraft.substring(cursorPos);
                                                    setDescDraft(textBefore + placeholder + textAfter);

                                                    try {
                                                        const uploaded = await api.uploadFile(file);
                                                        // For tickets, we add to attachments array
                                                        const currentAttachments = ticket.attachments || [];
                                                        updateTicket(ticket.id, {
                                                            attachments: [...currentAttachments, uploaded]
                                                        });

                                                        // Also update inline placeholder
                                                        const finalMarkdown = `\n![${fileName}](${uploaded.url})\n`;
                                                        setDescDraft(prev => prev.replace(placeholder, finalMarkdown));
                                                    } catch (err) {
                                                        console.error("Paste upload failed", err);
                                                        setDescDraft(prev => prev.replace(placeholder, `\n[Upload Failed: ${fileName}]\n`));
                                                    }
                                                }}
                                                autoFocus
                                            />
                                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                <button
                                                    className="btn btn-ghost btn-sm"
                                                    onClick={() => setIsEditingDesc(false)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => {
                                                        updateTicket(ticket.id, { description: descDraft });
                                                        setIsEditingDesc(false);
                                                    }}
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="message-text">
                                            <Markdown text={ticket.description || ''} />
                                        </div>
                                    )}

                                    {ticket.attachments && ticket.attachments.length > 0 && (
                                        <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {ticket.attachments.map((att: any, i: number) => {
                                                const fileName = att.original_name || att.name || 'File';
                                                const fileSize = att.size_bytes || att.size || 0;
                                                const isImage = att.mime_type?.startsWith('image/') || att.type?.startsWith('image/');
                                                const authToken = localStorage.getItem('auth_token');

                                                let fileUrl = (authToken && (att.url.includes('/api/files/') || att.url.startsWith('/api/')))
                                                    ? `${att.url}${att.url.includes('?') ? '&' : '?'}token=${encodeURIComponent(authToken)}`
                                                    : att.url;

                                                if (fileUrl.startsWith('/')) {
                                                    const apiBase = (window as any).API_BASE || 'http://localhost:3000';
                                                    fileUrl = `${apiBase}${fileUrl}`;
                                                }

                                                // Download handler
                                                const handleDownload = (e: React.MouseEvent, url: string, name: string) => {
                                                    if (url.startsWith('blob:') || url.includes('token=')) return;
                                                    e.preventDefault();
                                                    fetch(url).then(res => res.blob()).then(blob => {
                                                        const blobUrl = window.URL.createObjectURL(blob);
                                                        const link = document.createElement('a');
                                                        link.href = blobUrl;
                                                        link.download = name;
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        window.URL.revokeObjectURL(blobUrl);
                                                        document.body.removeChild(link);
                                                    });
                                                };

                                                return isImage ? (
                                                    <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                                                        <img
                                                            src={fileUrl}
                                                            alt={fileName}
                                                            style={{ maxWidth: 200, borderRadius: 8, border: '1px solid var(--border-color)', cursor: 'zoom-in', display: 'block' }}
                                                            onClick={() => setLightboxUrl(fileUrl)}
                                                            title="Click to enlarge"
                                                        />
                                                        <a
                                                            href={fileUrl}
                                                            download={fileName}
                                                            style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.55)', borderRadius: 4, padding: '2px 4px', display: 'flex', alignItems: 'center', color: 'white' }}
                                                            title="Download"
                                                            onClick={e => handleDownload(e, fileUrl, fileName)}
                                                        >
                                                            <Download size={12} />
                                                        </a>
                                                    </div>
                                                ) : (
                                                    <a key={i} href={fileUrl} rel="noopener noreferrer" style={{
                                                        display: 'flex', alignItems: 'center', gap: 8,
                                                        background: 'var(--bg-secondary)', padding: '8px 12px',
                                                        borderRadius: 6, textDecoration: 'none', color: 'var(--text-primary)',
                                                        border: '1px solid var(--border-color)', fontSize: 13
                                                    }} onClick={(e) => handleDownload(e, fileUrl, fileName)}>
                                                        <FileIcon size={16} />
                                                        <span>{fileName}</span>
                                                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                                            ({fileSize > 0 ? `${Math.round(fileSize / 1024)}KB` : 'Size unknown'})
                                                        </span>
                                                        <Download size={14} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Subtasks Section */}
                        <div style={{ padding: '0 var(--spacing-md)', marginBottom: 'var(--spacing-lg)', flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Subtasks</h4>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {(ticket.subtasks || []).map((subtask: Subtask) => (
                                    <div
                                        key={subtask.id}
                                        className={`subtask-item ${draggedSubtask === subtask.id ? 'dragging' : ''}`}
                                        draggable
                                        onDragStart={() => setDraggedSubtask(subtask.id)}
                                        onDragEnd={() => setDraggedSubtask(null)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => {
                                            if (draggedSubtask && draggedSubtask !== subtask.id) {
                                                handleSubtaskReorder(draggedSubtask, subtask.id);
                                            }
                                        }}
                                    >
                                        <GripVertical size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, cursor: 'grab' }} />
                                        <div
                                            onClick={() => handleToggleSubtask(subtask.id)}
                                            className={`subtask-checkbox ${subtask.completed ? 'completed' : ''}`}
                                        >
                                            {subtask.completed && <Check size={10} color="white" strokeWidth={3} />}
                                        </div>
                                        <input
                                            type="text"
                                            defaultValue={subtask.title}
                                            onBlur={(e) => handleSubtaskChange(subtask.id, e.target.value)}
                                            placeholder="Describe this subtask..."
                                            className={`subtask-input ${subtask.completed ? 'completed' : ''}`}
                                        />
                                        <button
                                            onClick={() => handleDeleteSubtask(subtask.id)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: 4,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 4,
                                                opacity: 0.5
                                            }}
                                            className="subtask-delete-btn"
                                            title="Delete subtask"
                                        >
                                            <Trash2 size={14} color="var(--text-muted)" />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleAddSubtask}
                                style={{ marginTop: 8, display: 'flex', gap: 6 }}
                            >
                                <Plus size={14} />
                                Add subtask
                            </button>
                        </div>

                        {/* Spacer to push comments down if there are few items above */}
                        <div style={{ flex: 1 }} />

                        {/* Comments Section */}
                        <div style={{
                            borderTop: '1px solid var(--border-color)',
                            marginTop: 'var(--spacing-lg)',
                            paddingTop: 'var(--spacing-md)',
                            flexShrink: 0
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 'var(--spacing-md)',
                                paddingLeft: 'var(--spacing-md)',
                                paddingRight: 'var(--spacing-md)',
                            }}>
                                <MessageSquare size={15} strokeWidth={1.5} style={{ color: 'var(--text-secondary)' }} />
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.02em' }}>Comments</span>
                                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>({messages.length})</span>
                            </div>

                            {messages.length === 0 ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: 'var(--spacing-lg)',
                                    color: 'var(--text-muted)',
                                    fontSize: 13
                                }}>
                                    No comments yet — be the first to comment.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {messages.map(message => (
                                        <div key={message.id} className="message ticket-comment-row" style={{ padding: '6px var(--spacing-md)' }}>
                                            <div
                                                className="avatar avatar-sm"
                                                style={{
                                                    background: getAvatarColor(message.author_id || message.author?.id || ''),
                                                    color: '#fff',
                                                    flexShrink: 0,
                                                    alignSelf: 'flex-start',
                                                    marginTop: 2
                                                }}
                                            >
                                                {getInitials(message.author?.name)}
                                            </div>
                                            <div className="message-content">
                                                <div className="message-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <span className="message-author">{message.author?.name}</span>
                                                        <span className="message-time">{formatTime(message.created_at)}</span>
                                                        {message.is_edited && (
                                                            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>(edited)</span>
                                                        )}
                                                    </div>

                                                    {(isAdmin || currentUser?.id === message.author_id) && !editingCommentId && !message.is_deleted && (
                                                        <div className="ticket-comment-actions" style={{ position: 'relative' }}>
                                                            <button
                                                                className="btn btn-ghost btn-icon btn-xs"
                                                                title="More actions"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setOpenCommentMenuId(openCommentMenuId === message.id ? null : message.id);
                                                                }}
                                                            >
                                                                <MoreVertical size={14} />
                                                            </button>
                                                            {openCommentMenuId === message.id && (
                                                                <div
                                                                    ref={commentMenuRef}
                                                                    style={{
                                                                        position: 'absolute',
                                                                        right: 0,
                                                                        top: '100%',
                                                                        zIndex: 100,
                                                                        background: 'var(--bg-primary)',
                                                                        border: '1px solid var(--border-color)',
                                                                        borderRadius: 8,
                                                                        boxShadow: 'var(--shadow-lg)',
                                                                        minWidth: 140,
                                                                        overflow: 'hidden'
                                                                    }}
                                                                >
                                                                    <button
                                                                        className="btn btn-ghost"
                                                                        style={{ width: '100%', justifyContent: 'flex-start', gap: 8, padding: '8px 12px', fontSize: 13, borderRadius: 0 }}
                                                                        onClick={() => { setEditingCommentId(message.id); setOpenCommentMenuId(null); }}
                                                                    >
                                                                        <Edit2 size={13} /> Edit
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-ghost"
                                                                        style={{ width: '100%', justifyContent: 'flex-start', gap: 8, padding: '8px 12px', fontSize: 13, borderRadius: 0, color: 'var(--color-danger)' }}
                                                                        onClick={() => { handleDeleteComment(message.id); setOpenCommentMenuId(null); }}
                                                                    >
                                                                        <Trash2 size={13} /> Delete
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {editingCommentId === message.id ? (
                                                    <div style={{ marginTop: 8 }}>
                                                        <MessageComposer
                                                            initialContent={message.content}
                                                            initialAttachments={message.attachments || []}
                                                            onSend={(content: string, enc: any, atts: any) => handleUpdateComment(message.id, content, enc, atts)}
                                                            onCancel={() => setEditingCommentId(null)}
                                                            isEditing={true}
                                                            submitLabel="Save Changes"
                                                        />
                                                    </div>
                                                ) : message.is_deleted ? (
                                                    <div className="message-text" style={{ fontStyle: 'italic', color: 'var(--text-muted)', opacity: 0.7 }}>
                                                        This message was deleted
                                                    </div>
                                                ) : (
                                                    <div className="message-text">
                                                        <Markdown text={message.content || ''} />
                                                    </div>
                                                )}

                                                {/* Attachments Preview for Comment */}
                                                {!editingCommentId && !message.is_deleted && message.attachments && message.attachments.length > 0 && (
                                                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                        {message.attachments.map((att: any, idx: number) => {
                                                            const fileName = att.original_name || att.name || 'File';
                                                            const isImage = att.mime_type?.startsWith('image/') || att.type?.startsWith('image/');
                                                            let url = att.url;
                                                            if (url && !url.startsWith('http') && !url.startsWith('/')) {
                                                                url = `/api/files/${att.id}`;
                                                            }
                                                            // Add token if needed
                                                            const authToken = localStorage.getItem('auth_token');
                                                            if (url && authToken && !url.includes('token=')) {
                                                                url += (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(authToken);
                                                            }

                                                            return isImage ? (
                                                                <img
                                                                    key={idx}
                                                                    src={url}
                                                                    alt={fileName}
                                                                    style={{ maxHeight: 100, borderRadius: 4, border: '1px solid var(--border-color)', cursor: 'pointer' }}
                                                                    onClick={() => setLightboxUrl(url)}
                                                                />
                                                            ) : (
                                                                <a key={idx} href={url} target="_blank" rel="noopener noreferrer" style={{
                                                                    fontSize: 11, padding: '4px 8px', background: 'var(--bg-tertiary)', borderRadius: 4, textDecoration: 'none', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 4
                                                                }}>
                                                                    <FileIcon size={12} /> {fileName}
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* Read Receipts */}
                                                {settings?.enable_read_receipts && message.author_id === currentUser?.id && (() => {
                                                    const viewers = message.read_by?.filter((r: any) => r.user_id !== message.author_id) || [];
                                                    const uniqueViewerIds = [...new Set(viewers.map((r: any) => r.user_id))] as string[];
                                                    const viewerNames = uniqueViewerIds.map((id) => users.find(u => u.id === id)?.name).filter(Boolean).join(', ');
                                                    if (uniqueViewerIds.length === 0) return null;
                                                    return (
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                                                            <div
                                                                title={`Read by: ${viewerNames}`}
                                                                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)', cursor: 'default' }}
                                                            >
                                                                <Eye size={12} />
                                                                {uniqueViewerIds.length}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>{/* closes .message-list */}

                    {/* Composer */}
                    <MessageComposer
                        placeholder="Add a comment..."
                        onSend={(content: string, encryption: boolean, attachments: any[]) => handleSendMessage(content, encryption, attachments)}
                    />
                </div>{/* closes .ticket-detail-main */}

                {/* Sidebar - Ticket details */}
                <div className="ticket-detail-sidebar">
                    {/* Assignees */}
                    <div className="ticket-sidebar-section">
                        <h4 className="ticket-sidebar-title">
                            Assignees {!settings?.allow_multiple_assignees && <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-muted)' }}>(Single)</span>}
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                            {/* Display current assignees */}
                            {(ticket.assignees || []).length === 0 && !ticket.assigned_to_department_id && (
                                <p style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Unassigned</p>
                            )}

                            {/* Current Individual Assignees */}
                            {(ticket.assignees || []).map((assignee: any) => {
                                const user = users.find(u => u.id === assignee.id) || assignee;
                                return (
                                    <div key={user.id} className="user-list-item active" style={{
                                        background: 'var(--accent-primary)', color: 'white'
                                    }}>
                                        <div className="avatar avatar-sm">{user.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || '?'}</div>
                                        <span style={{ fontSize: 'var(--font-size-sm)' }}>{user.name}</span>
                                        <X size={14} style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={() => handleAssigneeChange(user.id)} />
                                    </div>
                                );
                            })}

                            {/* Current Queue Assignee */}
                            {ticket.assigned_to_department_id && (() => {
                                const dept = departments.find(d => d.id === ticket.assigned_to_department_id);
                                if (!dept) return null;
                                return (
                                    <div className="user-list-item active" style={{
                                        background: 'var(--accent-primary)', color: 'white', border: `1px dashed ${dept.color}`
                                    }}>
                                        <FolderOpen size={14} color="#fff" />
                                        <span style={{ fontSize: 'var(--font-size-sm)' }}>Assigned to {dept.name} Queue</span>
                                        {canManageRouting && (
                                            <X size={14} style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={() => updateTicket(ticket.id, { assigned_to_department_id: null })} />
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Search / Add Dropdown */}
                            {(canAssignIndividuals || canManageRouting) && (
                            <div style={{ position: 'relative', marginTop: 8 }} ref={assigneeSearchRef}>
                                <div
                                    className="input"
                                    style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', cursor: 'text', height: 32 }}
                                    onClick={() => setAssigneeSearchOpen(true)}
                                >
                                    <Search size={14} style={{ color: 'var(--text-muted)', marginRight: 8 }} />
                                    <input
                                        placeholder={canAssignIndividuals ? 'Add assignee...' : 'Assign to department queue...'}
                                        style={{ border: 'none', background: 'transparent', outline: 'none', flex: 1, fontSize: 13, color: 'inherit' }}
                                        value={assigneeSearchTerm}
                                        onChange={(e) => {
                                            setAssigneeSearchTerm(e.target.value);
                                            setAssigneeSearchOpen(true);
                                        }}
                                        onFocus={() => setAssigneeSearchOpen(true)}
                                    />
                                </div>
                                {assigneeSearchOpen && (
                                    <div style={{
                                        position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                                        background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                                        borderRadius: 8, maxHeight: 250, overflowY: 'auto', zIndex: 1000,
                                        boxShadow: 'var(--shadow-lg)', padding: 8
                                    }}>
                                        {!assigneeSearchTerm.trim() ? (
                                            <div style={{ padding: '8px', color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
                                                Type to search users...
                                            </div>
                                        ) : (() => {
                                            const searchTerm = assigneeSearchTerm.toLowerCase();
                                            // Find matching users and queue departments
                                            const matchingUsers = canAssignIndividuals
                                                ? users.filter(u => u.name.toLowerCase().includes(searchTerm))
                                                : [];
                                            const matchingDepts = canManageRouting ? departments.filter(d =>
                                                d.allow_external_individual_assignment === false &&
                                                d.name.toLowerCase().includes(searchTerm)
                                            ) : [];

                                            if (matchingUsers.length === 0 && matchingDepts.length === 0) {
                                                return <div style={{ padding: '8px', color: 'var(--text-muted)', fontSize: 13 }}>No results found</div>;
                                            }

                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    {matchingDepts.map(dept => (
                                                        <div key={`dept-${dept.id}`} className="user-list-item" onClick={() => {
                                                            updateTicket(ticket.id, { assigned_to_department_id: dept.id, assignee_ids: [] });
                                                            setAssigneeSearchTerm('');
                                                            setAssigneeSearchOpen(false);
                                                        }}>
                                                            <FolderOpen size={14} color={dept.color} />
                                                            <span style={{ fontSize: 13 }}>Assign to {dept.name} Queue</span>
                                                        </div>
                                                    ))}
                                                    {matchingUsers.map(u => (
                                                        <div key={u.id} className="user-list-item" onClick={() => {
                                                            handleAssigneeChange(u.id);
                                                            setAssigneeSearchTerm('');
                                                            setAssigneeSearchOpen(false);
                                                        }}>
                                                            <div className="avatar avatar-sm">{u.name?.charAt(0).toUpperCase() || '?'}</div>
                                                            <span style={{ fontSize: 13 }}>{u.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                            )}
                        </div>
                    </div>

                    {/* Collaborators */}
                    <div className="ticket-sidebar-section">
                        <h4 className="ticket-sidebar-title">
                            <Users size={14} />
                            Collaborators
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                            {/* Display current collaborators */}
                            {(ticket.collaborators || []).length === 0 && !(currentUser?.id === ticket.created_by || isAdmin) && (
                                <p style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No collaborators</p>
                            )}
                            {(ticket.collaborators || []).map((collab: any) => {
                                const user = users.find(u => u.id === collab.id) || collab;
                                return (
                                    <div key={collab.id} className="user-list-item active" style={{
                                        background: 'rgba(79, 70, 229, 0.1)',
                                        color: '#818cf8',
                                        border: '1px solid rgba(79, 70, 229, 0.2)',
                                        cursor: (currentUser?.id === ticket.created_by || isAdmin) ? 'pointer' : 'default'
                                    }}>
                                        <div className="avatar avatar-sm">{user.name?.charAt(0).toUpperCase() || '?'}</div>
                                        <span style={{ fontSize: 'var(--font-size-sm)' }}>{user.name}</span>
                                        {(currentUser?.id === ticket.created_by || isAdmin) && (
                                            <X size={14} style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={() => handleCollaboratorChange(collab.id)} />
                                        )}
                                    </div>
                                );
                            })}

                            {/* Picker: only visible to creator/admin to add users not yet collaborators */}
                            {(currentUser?.id === ticket.created_by || isAdmin) && (
                                <div style={{ position: 'relative', marginTop: 8 }} ref={collabSearchRef}>
                                    <div
                                        className="input"
                                        style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', cursor: 'text', height: 32 }}
                                        onClick={() => setCollabSearchOpen(true)}
                                    >
                                        <Search size={14} style={{ color: 'var(--text-muted)', marginRight: 8 }} />
                                        <input
                                            placeholder="Add collaborator..."
                                            style={{ border: 'none', background: 'transparent', outline: 'none', flex: 1, fontSize: 13, color: 'inherit' }}
                                            value={collabSearchTerm}
                                            onChange={(e) => {
                                                setCollabSearchTerm(e.target.value);
                                                setCollabSearchOpen(true);
                                            }}
                                            onFocus={() => setCollabSearchOpen(true)}
                                        />
                                    </div>
                                    {collabSearchOpen && (
                                        <div style={{
                                            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                                            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                                            borderRadius: 8, maxHeight: 250, overflowY: 'auto', zIndex: 1000,
                                            boxShadow: 'var(--shadow-lg)', padding: 8
                                        }}>
                                            {!collabSearchTerm.trim() ? (
                                                <div style={{ padding: '8px', color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
                                                    Type to search users...
                                                </div>
                                            ) : (() => {
                                                const searchTerm = collabSearchTerm.toLowerCase();
                                                const matchingUsers = users.filter(u =>
                                                    u.name.toLowerCase().includes(searchTerm) &&
                                                    !ticket.collaborators?.some((c: any) => c.id === u.id)
                                                );

                                                if (matchingUsers.length === 0) {
                                                    return <div style={{ padding: '8px', color: 'var(--text-muted)', fontSize: 13 }}>No results found</div>;
                                                }

                                                return (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        {matchingUsers.map(u => (
                                                            <div key={u.id} className="user-list-item" onClick={() => {
                                                                handleCollaboratorChange(u.id);
                                                                setCollabSearchTerm('');
                                                                setCollabSearchOpen(false);
                                                            }}>
                                                                <div className="avatar avatar-sm">{u.name?.charAt(0).toUpperCase() || '?'}</div>
                                                                <span style={{ fontSize: 13 }}>{u.name}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Due Date */}
                    <div className="ticket-sidebar-section">
                        <h4 className="ticket-sidebar-title">
                            <CalendarDays size={14} />
                            Due Date
                        </h4>
                        <input
                            type="date"
                            className="input"
                            value={ticket.due_date ? ticket.due_date.split('T')[0] : ''}
                            onChange={(e) => updateTicket(ticket.id, { due_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                            style={{
                                fontSize: 'var(--font-size-sm)',
                                width: '100%',
                                color: ticket.due_date && new Date(ticket.due_date) < new Date() && ticket.status !== 'closed'
                                    ? 'var(--accent-danger)'
                                    : 'var(--text-primary)'
                            }}
                        />
                    </div>


                    {/* Activity Log */}
                    {(() => {
                        const visibility = settings?.activity_log_visibility || 'all';
                        const userRoleName = useApp().currentUser?.role?.name?.toLowerCase() || '';
                        const isAdminUser = typeof userRoleName === 'string' && userRoleName.includes('admin');
                        const isDeptHead = departments.some(d => d.head_ids?.includes(useApp().currentUser?.id || ''));

                        const canViewActivityLog = visibility === 'all' ||
                            (visibility === 'department_heads' && (isDeptHead || isAdminUser)) ||
                            (visibility === 'admins' && isAdminUser);

                        if (!canViewActivityLog || !ticket.activity_log || ticket.activity_log.length === 0) return null;

                        return (
                            <div className="ticket-sidebar-section">
                                <h4 className="ticket-sidebar-title">
                                    <History size={14} />
                                    Activity Log
                                </h4>
                                <div style={{
                                    maxHeight: 200,
                                    overflow: 'auto',
                                    background: 'var(--bg-primary)',
                                    borderRadius: 'var(--radius-md)',
                                    padding: 'var(--spacing-sm)'
                                }}>
                                    {ticket.activity_log.slice().reverse().map((entry: any, idx: number) => (
                                        <div key={entry.id || idx} style={{
                                            padding: 'var(--spacing-xs) 0',
                                            borderBottom: idx < ticket.activity_log.length - 1 ? '1px solid var(--border-color)' : 'none',
                                            fontSize: 'var(--font-size-xs)'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                                <span style={{ color: 'var(--text-muted)' }}>
                                                    {users.find(u => u.id === entry.user_id)?.name || 'Unknown'}
                                                </span>
                                                <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>
                                                    {new Date(entry.timestamp).toLocaleString()}
                                                </span>
                                            </div>
                                            <div style={{ color: 'var(--text-primary)' }}>
                                                {entry.action === 'created' && 'Created this ticket'}
                                                {entry.action === 'status_changed' && `Changed status: ${entry.details?.from} → ${entry.details?.to}`}
                                                {entry.action === 'priority_changed' && `Changed priority: ${entry.details?.from} → ${entry.details?.to}`}
                                                {entry.action === 'assigned' && `Assigned to ${entry.details?.user_name}`}
                                                {entry.action === 'due_date_set' && `Set due date: ${entry.details?.to}`}
                                                {entry.action === 'tag_added' && `Added tag: ${entry.details?.value}`}
                                                {entry.action === 'tag_removed' && `Removed tag: ${entry.details?.value}`}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
            {/* Lightbox Overlay */}
            {lightboxUrl && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'zoom-out'
                    }}
                    onClick={() => setLightboxUrl(null)}
                >
                    <img
                        src={lightboxUrl}
                        style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
                        alt="Enlarged"
                    />
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                title="Delete Ticket"
                message={`Are you sure you want to delete ticket #${ticket.ticket_number || ticket.id.slice(0, 4)}?`}
                confirmText="Delete"
                onConfirm={() => {
                    deleteTicket(ticket.id).catch(err => console.error('Failed to delete ticket:', err));
                }}
            />
        </div>
    );
}
